import { type ReactNode, useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import ResearchNav from '@/components/admin/research/ResearchNav'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Plus } from 'lucide-react'
import type { ResearchEventSummary } from '@/types/research'

type Filters = {
  search: string
  event_type: string
  state: string
  status: string
  year: string
  archived: string
}

type Props = {
  events: ResearchEventSummary[]
  filters: Filters
  filter_options: {
    event_types: string[]
    states: string[]
    statuses: string[]
    years: number[]
  }
}

const ANY = '__any__'

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—'
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export default function ResearchEventsIndex({ events, filters, filter_options }: Props) {
  const [search, setSearch] = useState(filters.search)
  const [deleteTarget, setDeleteTarget] = useState<ResearchEventSummary | null>(null)

  function applyFilters(next: Partial<Filters>) {
    const merged = { ...filters, search, ...next }
    router.get(
      '/admin/research/events',
      Object.fromEntries(Object.entries(merged).filter(([, v]) => v !== '')),
      { preserveState: true },
    )
  }

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    applyFilters({})
  }

  function confirmDelete() {
    if (!deleteTarget) return
    router.delete(`/admin/research/events/${deleteTarget.id}`)
    setDeleteTarget(null)
  }

  const archivedView = Boolean(filters.archived)

  function filterSelect(
    label: string,
    key: keyof Filters,
    options: (string | number)[],
  ) {
    return (
      <Select
        value={filters[key] || ANY}
        onValueChange={value => applyFilters({ [key]: value === ANY ? '' : value })}
      >
        <SelectTrigger className="w-36" size="sm">
          <SelectValue placeholder={label} />
        </SelectTrigger>
        <SelectContent position="popper" align="end">
          <SelectGroup>
            <SelectItem value={ANY}>{label}: All</SelectItem>
            {options.map(option => (
              <SelectItem key={option} value={String(option)}>
                {option}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    )
  }

  return (
    <>
      <Head title="Research - Events" />
      <div>
        <h1 className="text-3xl font-semibold tracking-tight font-serif">Research Database</h1>
        <div className="mt-4">
          <ResearchNav />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <form onSubmit={handleSearch} className="max-w-sm flex-1">
            <Input
              placeholder="Search by event or organization..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </form>
          {filterSelect('Type', 'event_type', filter_options.event_types)}
          {filterSelect('State', 'state', filter_options.states)}
          {filterSelect('Status', 'status', filter_options.statuses)}
          {filterSelect('Year', 'year', filter_options.years)}
          <div className="flex gap-1 border-l pl-3">
            <Button
              variant={!archivedView ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => applyFilters({ archived: '' })}
            >
              Active
            </Button>
            <Button
              variant={archivedView ? 'secondary' : 'outline'}
              size="sm"
              onClick={() => applyFilters({ archived: '1' })}
            >
              Archived
            </Button>
          </div>
          <Button asChild>
            <Link href="/admin/research/events/new">
              <Plus className="mr-2 size-4" />
              New Event
            </Link>
          </Button>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-black/10 bg-card shadow-xs">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Event</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Location</TableHead>
                <TableHead className="text-center">Sponsors</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map(event => (
                <TableRow key={event.id}>
                  <TableCell className="font-medium">
                    <Link href={`/admin/research/events/${event.id}`} className="hover:underline">
                      {event.name || 'Untitled'}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    <Link
                      href={`/admin/research/organizations/${event.organization_id}`}
                      className="hover:underline"
                    >
                      {event.organization_name}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{event.event_type || '—'}</TableCell>
                  <TableCell className="text-muted-foreground">{formatDate(event.date)}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {[event.city, event.state].filter(Boolean).join(', ') || '—'}
                  </TableCell>
                  <TableCell className="text-center">{event.sponsor_count}</TableCell>
                  <TableCell>
                    {event.status ? <Badge variant="secondary">{event.status}</Badge> : '—'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button asChild variant="ghost" size="sm">
                        <Link href={`/admin/research/events/${event.id}`}>View</Link>
                      </Button>
                      {archivedView ? (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => router.patch(`/admin/research/events/${event.id}/restore`)}
                          >
                            Restore
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setDeleteTarget(event)}
                          >
                            Delete
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button asChild variant="ghost" size="sm">
                            <Link href={`/admin/research/events/${event.id}/edit`}>Edit</Link>
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground"
                            onClick={() => router.patch(`/admin/research/events/${event.id}/archive`)}
                          >
                            Archive
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {events.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">
                    {archivedView ? 'No archived events.' : 'No research events found.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Permanently delete research event</DialogTitle>
            <DialogDescription>
              Permanently delete &ldquo;{deleteTarget?.name || 'this event'}&rdquo;? This removes
              all its honorees, sponsorships, packages, tickets, and journal ads. This cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

ResearchEventsIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
